import { useMemo } from 'react';
import Box from '@mui/material/Box';
import useGetMedias from '@/hooks/useGetMedias';
import {
    findForwarderNodeConfigurationForm,
    fixTypeNoteConfigurationVersion2AsVersion1
} from '@/utils/common';
import NoteTitle from '@/components/common/notes/note-title/note-title';
import ShowNoteContent from '@/components/common/notes/show-note-content/show-note-content';
import ShowNoteImages from '@/components/common/notes/show-note-images/show-note-images';
import ShowNoteAudios from '@/components/common/notes/show-note-audios/show-note-audios';
import ShowNoteDocuments from '@/components/common/notes/show-note-documents/show-note-documents';
import ShowNoteSigns from '@/components/common/notes/show-note-signs/show-note-signs';
import ShowNoteExternalIdentification from '@/components/common/notes/show-note-external-identification/show-note-external-identification';
import ShowNoteForms from '@/components/common/notes/show-note-forms/show-note-forms';
import { resolveIcon } from '@/utils/icons';

interface Props {
    note: {
        iconId?: string;
        name: string;
        createdAt: string;
        data: {
            auth: {
                firstName: string;
                lastName: string;
                username: string;
            };
            customFields: {
                coords: number[];
                forms: any;
                external_identification: any;
            };
            medias: {
                customFields: any;
                id: string;
            }[];
            signatures: any[];
        };
        customFields: any;
        forms: any;
        coords: any;
        configuration: {
            customFields: any;
            isForwarded: boolean;
        };
        typeNote: any;
    };
}

const GlobalNoteTraceability = ({ note }: Props) => {
    const title = useMemo(() => note?.name, [note]);

    const time = useMemo(() => note?.createdAt, [note]);

    const coords: any = useMemo(() => {
        const tmp = note?.data?.customFields?.coords;

        if (!tmp) return undefined;

        return Array.isArray(tmp) && tmp.length > 0
            ? {
                  latitude: tmp[0],
                  longitude: tmp[1],
              }
            : tmp;
    }, [note]);

    const authInfo = useMemo(() => note?.data?.auth, [note]);

    const content = useMemo(() => note?.data?.customFields?.forms?._note_?.content, [note]);

    const icon = useMemo(() => resolveIcon(note.iconId, 'LinearScale'), [note]);

    const mediasIds = useMemo(() => note?.data?.medias.map((media) => media.id), [note]);
    const { images, audios, documents, isLoading: isLoadingMedias } = useGetMedias({ mediasIds });

    const signs = useMemo(() => note?.data?.signatures, [note]);

    const externalIdentification = useMemo(() => note?.data?.customFields?.external_identification, [note]);

    const formsData = useMemo(
        () => fixTypeNoteConfigurationVersion2AsVersion1(note?.typeNote, note?.data?.customFields?.forms),
        [note],
    );
    const formsOrigin = useMemo(
        () =>
            note?.configuration?.isForwarded
                ? findForwarderNodeConfigurationForm(note.configuration.customFields)
                : note?.configuration?.customFields?.componentForms,
        [note],
    );

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                backgroundColor: 'white',
                py: 1,
                borderRadius: 2,
            }}
        >
            <NoteTitle icon={icon} title={title} time={time} authInfo={authInfo} coords={coords} />

            {content && <ShowNoteContent content={content} />}

            {!isLoadingMedias && images && images.length > 0 && (
                <ShowNoteImages imagesUrls={images.map((item) => item.urlToDownload)} />
            )}

            {!isLoadingMedias && audios && audios.length > 0 && (
                <ShowNoteAudios audios={audios.map((item) => item.urlToDownload)} />
            )}

            {!isLoadingMedias && documents && documents.length > 0 && (
                <ShowNoteDocuments
                    documents={documents.map((item) => ({
                        urlToDownload: item.urlToDownload,
                        name: item.name,
                    }))}
                />
            )}

            {signs && signs.length > 0 && <ShowNoteSigns signs={signs} />}

            {externalIdentification && (
                <ShowNoteExternalIdentification externalIdentification={externalIdentification} />
            )}

            {formsData && formsOrigin && formsOrigin.length > 0 && (
                <ShowNoteForms formsData={formsData} formsOrigin={formsOrigin} typeNote={note?.typeNote} />
            )}
        </Box>
    );
};

export default GlobalNoteTraceability;
